<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import { api } from '../../lib/api/client';
  import type { NearbyPlace, PlaceCategory, PlaceChoice } from '../../lib/api/types';
  import Modal from '../../lib/components/Modal.svelte';
  import { integer, t } from '../../lib/i18n/index.ts';
  import { categories, categoryIcon, categoryLabel } from './format';

  // PlacePicker picks where a stay was: a nearby place (the member's own
  // first), or a name of their own (005 FR-041, 005 FR-020). Filling in a missing
  // visit also sets its times.
  let { title, lat, lon, start, end, withTimes, onsave, onclose }: {
    title: string;
    lat: number;
    lon: number;
    start?: string;
    end?: string;
    withTimes: boolean;
    onsave: (choice: PlaceChoice, remember: boolean, start?: string, end?: string) => Promise<void>;
    onclose: () => void;
  } = $props();

  let nearby = $state<NearbyPlace[]>([]);
  let picked = $state(-1);
  let name = $state('');
  let category = $state<PlaceCategory>('other');
  let remember = $state(true);
  let from = $state(untrack(() => (start ? hhmm(start) : '')));
  let to = $state(untrack(() => (end ? hhmm(end) : '')));
  let busy = $state(false);
  let error = $state('');

  onMount(async () => {
    nearby = await api.get<NearbyPlace[]>(`/me/timeline/nearby?lat=${lat}&lon=${lon}`).catch(() => []);
  });

  function hhmm(iso: string): string {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  // at puts a time of day on the local day of base.
  function at(base: string, time: string): string {
    const d = new Date(base);
    const [h, m] = time.split(':').map(Number);
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  }

  // keepOr keeps a time the member left alone to the second, since the time
  // field shows only minutes and the stays around a gap end mid-minute.
  function keepOr(iso: string, time: string): string {
    return hhmm(iso) === time ? iso : at(iso, time);
  }

  async function save() {
    let choice: PlaceChoice;
    if (picked >= 0) {
      const { source: _s, distance_m: _d, ...c } = nearby[picked];
      choice = c;
    } else if (name.trim()) {
      choice = { name: name.trim(), category, lat, lon };
    } else {
      error = t('timelinePickPlace');
      return;
    }
    busy = true;
    error = '';
    try {
      await onsave(choice, remember, withTimes && start ? keepOr(start, from) : undefined, withTimes && end ? keepOr(end, to) : undefined);
      onclose();
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }

</script>

<Modal {title} {onclose} maxWidth="lg">
  <form class="flex flex-col gap-3" onsubmit={(e: SubmitEvent) => { e.preventDefault(); save(); }}>
    {#if withTimes}
      <div class="flex gap-2 items-center">
        <label class="text-sm" for="pp-from">{t('timelineFrom')}</label>
        <input id="pp-from" type="time" class="input" bind:value={from} required />
        <label class="text-sm" for="pp-to">{t('timelineTimeTo')}</label>
        <input id="pp-to" type="time" class="input" bind:value={to} required />
      </div>
    {/if}
    {#if nearby.length > 0}
      <ul class="max-h-64 overflow-y-auto">
        {#each nearby as p, i (i)}
          <li>
            <label class="flex items-center gap-3 py-1.5 cursor-pointer">
              <input type="radio" name="pp-place" value={i} bind:group={picked} />
              <i aria-hidden="true" class="las {categoryIcon(p.category)} text-lg"></i>
              <span class="flex-1 min-w-0">
                <span class="block truncate">{p.name}</span>
                <span class="block text-xs text-text-muted truncate">{[p.address, p.city].filter(Boolean).join(', ')}</span>
              </span>
              <span class="text-xs text-text-muted">{t('timelineNearbyMeta', { source: t('timelineSource', { source: p.source }), meters: integer(Math.round(p.distance_m)) })}</span>
            </label>
          </li>
        {/each}
      </ul>
    {/if}
    <label class="flex items-center gap-3 text-sm">
      <input type="radio" name="pp-place" value={-1} bind:group={picked} />
      {t('timelineNameIt')}
    </label>
    {#if picked === -1}
      <div class="flex gap-2">
        <input class="input flex-1" placeholder={t('timelinePlaceName')} bind:value={name} aria-label={t('timelinePlaceName')} />
        <select class="input w-48" bind:value={category} aria-label={t('timelineCategoryLabel')}>
          {#each categories as c (c)}<option value={c}>{categoryLabel(c)}</option>{/each}
        </select>
      </div>
    {/if}
    <label class="flex items-center gap-2 text-sm">
      <input type="checkbox" bind:checked={remember} />
      {t('timelineRemember')}
    </label>
    {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn btn-secondary" onclick={onclose}>{t('appCancel')}</button>
      <button type="submit" class="btn btn-primary" disabled={busy}>{busy ? t('timelineSaving') : t('appSave')}</button>
    </div>
  </form>
</Modal>
